/**
 * EncryptBinaryUseCase
 *
 * Pure-domain wrapper around `textEncryptionService.encryptBinary`. The binary
 * sibling of `EncryptTextUseCase` — it encrypts arbitrary ArrayBuffer payloads
 * (chat image / thumbnail bytes) with the SAME ECDH-derived AES-GCM key the
 * text use cases use, but with a random per-file IV prepended to the
 * ciphertext (the fixed-IV text surface is unsafe for media). Web-first
 * media-crypto design (no Flutter parity — consideration E).
 *
 * Consumers: chat image/thumbnail encryption (Task 10), video thumbnail
 * encryption (Task 11).
 *
 * Pre-condition: the singleton's AES-GCM key has been derived for the intended
 * peer via `InitializeTextEncryptionUseCase.execute`. When the key is null
 * (never-initialized, peer had no `publicJwk`, or derivation failed) the
 * service returns the input ArrayBuffer unchanged (pass-through).
 *
 * Service-singleton caveat: `textEncryptionService` has a single derived-key
 * slot shared with the text path. Any caller that interleaves
 * `initialize → encryptBinary` for different peers MUST serialize the pair —
 * peer B's `initialize` would clobber peer A's key before peer A's
 * `encryptBinary` ran. This use case does not own that serialization; the
 * caller does. See `InitializeTextEncryptionUseCase`'s JSDoc.
 */

import {
  TextEncryptionService,
  textEncryptionService,
} from '@/domain/services/textEncryption/textEncryptionService';

export class EncryptBinaryUseCase {
  private textEncryptionService: TextEncryptionService;

  constructor(service: TextEncryptionService = textEncryptionService) {
    this.textEncryptionService = service;
  }

  /**
   * Encrypt `data` using the singleton's currently-derived AES-GCM key.
   * Returns `[ iv (12 bytes) ‖ ciphertext ]` as a new ArrayBuffer. When the
   * derived key is null (pass-through mode) returns `data` unchanged. On a
   * SubtleCrypto failure the service rethrows — this use case does not catch.
   */
  async execute(data: ArrayBuffer): Promise<ArrayBuffer> {
    return this.textEncryptionService.encryptBinary(data);
  }
}

export const encryptBinaryUseCase = new EncryptBinaryUseCase();
